import { createServerFn } from '@tanstack/react-start';

import { apiClient } from '@/lib/api-client';
import { API_ROUTES } from '@/lib/api-config';

import { generateTattooTitle } from './ai-title-generator';

type CreateProjectPayload = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  description: string;
  size: string;
  bodyPlacement: string;
  customPlacement: string;
  inspirationText: string;
  inspirationUrls: string;
  inspirationImages: Array<string>; // Base64 data URLs
};

type ProjectImage = {
  id: string;
  project_id: string;
  image_url: string;
  created_at: string;
  updated_at: string | null;
};

type Project = {
  id: string;
  title: string;
  description: string;
  size: string | null;
  body_placement: string | null;
  custom_placement: string | null;
  inspiration_text: string | null;
  inspiration_urls: string | null;
  images: Array<ProjectImage>;
  status: {
    id: string;
    name: string;
    slug: string;
    color: string;
    display_order: number;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  client: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
  };
};

export const getProjectByIdServer = createServerFn({ method: 'GET' })
  .inputValidator((id: string) => id)
  .handler(async ({ data: id }) => {
    return apiClient.get<Project>(API_ROUTES.PUBLIC_PROJECT_BY_ID(id));
  });

type CreateProjectResponse = {
  success: boolean;
  data: {
    project_id: string;
    client_id: string;
  };
  error?: {
    message: string;
  };
};

export const createProjectServer = createServerFn({ method: 'POST' })
  .inputValidator((payload: CreateProjectPayload) => payload)
  .handler(async ({ data: payload }) => {
    const {
      firstName,
      lastName,
      email,
      phone,
      description,
      size,
      bodyPlacement,
      customPlacement,
      inspirationText,
      inspirationUrls,
      inspirationImages,
    } = payload;

    const finalPlacement = bodyPlacement === 'Autre' ? customPlacement : bodyPlacement;

    const title = await generateTattooTitle({
      description,
      size: size || undefined,
      placement: finalPlacement || undefined,
      inspirationText: inspirationText || undefined,
    });

    // Prepare form data for multipart submission
    const formData = new FormData();

    // Add JSON data as a single field
    formData.append(
      'data',
      JSON.stringify({
        client: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: phone || null,
        },
        project: {
          title,
          description,
          size: size || null,
          body_placement: bodyPlacement || null,
          custom_placement: customPlacement || null,
          inspiration_text: inspirationText || null,
          inspiration_urls: inspirationUrls || null,
        },
        send_notification: true,
      }),
    );

    // Add image files
    if (Array.isArray(inspirationImages)) {
      for (let i = 0; i < inspirationImages.length; i++) {
        const imageDataUrl = inspirationImages[i];
        // Convert base64 data URL to Blob
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();
        formData.append(`image_${i}`, blob, `inspiration_${i}.jpg`);
      }
    }

    const data = await apiClient.post<CreateProjectResponse['data']>(
      API_ROUTES.PUBLIC_PROJECT_CREATE,
      formData,
    );

    return { success: true, data } as CreateProjectResponse;
  });
